import { localePath, Locale, LOCALES, DEFAULT_LOCALE } from "../utils/locale";

export interface RouteMeta {
  title: { fr: string; en: string };
  description: { fr: string; en: string };
  ogImage?: string;
  /** Structured data for this page only (the site-wide Organization/WebSite is in index.html). */
  jsonLd?: (lang: Locale) => Record<string, unknown>;
}

export interface HeadOptions {
  /** For pages that must not be indexed, e.g. the 404. */
  noindex?: boolean;
}

const BASE_URL = "https://www.madarion.com";
const OG_LOCALE: Record<Locale, string> = { fr: "fr_FR", en: "en_GB" };

function setMeta(key: "name" | "property", value: string, content: string) {
  let el = document.querySelector(`meta[${key}="${value}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(key, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(key: "name" | "property", value: string) {
  document.querySelector(`meta[${key}="${value}"]`)?.remove();
}

function setLink(selector: string, attrs: Record<string, string>) {
  let el = document.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => (el as HTMLLinkElement).setAttribute(k, v));
}

/** Absolute URL of a page; the root keeps its trailing slash, as in the sitemap. */
export function absoluteUrl(basePath: string, lang: Locale): string {
  return `${BASE_URL}${localePath(basePath, lang)}`;
}

/**
 * Sets everything a crawler or a link preview reads, for one page in one language. `path` is the
 * page's language-neutral path ("/connectera"); each language's URL is derived from it.
 */
export function applyHead(meta: RouteMeta, locale: string, path: string, opts: HeadOptions = {}): void {
  const lang: Locale = locale === "en" ? "en" : "fr";
  const title = meta.title[lang];
  const description = meta.description[lang];
  const url = absoluteUrl(path, lang);

  document.title = title;
  document.documentElement.lang = lang;

  setMeta("name", "description", description);
  setMeta("property", "og:type", "website");
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", description);
  setMeta("property", "og:url", url);
  setMeta("property", "og:locale", OG_LOCALE[lang]);
  setMeta("property", "og:locale:alternate", OG_LOCALE[lang === "fr" ? "en" : "fr"]);
  if (meta.ogImage) {
    setMeta("property", "og:image", meta.ogImage);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("name", "twitter:image", meta.ogImage);
  }
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", description);

  if (opts.noindex) {
    setMeta("name", "robots", "noindex");
    document.querySelector('link[rel="canonical"]')?.remove();
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((l) => l.remove());
  } else {
    removeMeta("name", "robots");
    setLink('link[rel="canonical"]', { rel: "canonical", href: url });
    LOCALES.forEach((l) =>
      setLink(`link[rel="alternate"][hreflang="${l}"]`, { rel: "alternate", hreflang: l, href: absoluteUrl(path, l) }),
    );
    setLink('link[rel="alternate"][hreflang="x-default"]', {
      rel: "alternate",
      hreflang: "x-default",
      href: absoluteUrl(path, DEFAULT_LOCALE),
    });
  }

  const ld = document.getElementById("page-jsonld");
  if (meta.jsonLd && !opts.noindex) {
    const el = ld ?? Object.assign(document.createElement("script"), { id: "page-jsonld", type: "application/ld+json" });
    el.textContent = JSON.stringify(meta.jsonLd(lang));
    if (!ld) document.head.appendChild(el);
  } else {
    ld?.remove();
  }
}
